"use client";

import { useEffect, useRef } from "react";
import { EventNames, sendEvent, withSectionType } from "@/lib/utils/analytics";
import { DEFAULT_SCROLL_PERCENTAGE_THRESHOLDS } from "./scroll-percentage-thresholds";

type SectionConfig = {
  sectionType: string;
  /** Section root, e.g. `#user-review` (must match the section’s `id`). */
  selector: string;
  dwellMs?: number;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  /**
   * When true, also listens for horizontal scroll on the element with id
   * `{sectionId}-hscroll` inside this section (same base id as `selector`, + `-hscroll`).
   */
  trackHorizontalScroll?: boolean;
  /**
   * Milestones (0–100) for horizontal scroll depth, same as page scroll events.
   * Fires once per milestone per section rail.
   */
  horizontalScrollThresholds?: readonly number[];
  scrollEventName?: string;
  scrollEventProperties?: Record<string, unknown>;
};

interface SectionEngagementTrackerProps {
  sections: SectionConfig[];
}

function sectionIdFromSelector(selector: string) {
  return selector.trim().replace(/^#/, "");
}

function getHorizontalProgress(target: HTMLElement) {
  const maxScroll = target.scrollWidth - target.clientWidth;
  if (maxScroll <= 0) return 0;
  return target.scrollLeft / maxScroll;
}

export function SectionEngagementTracker({
  sections,
}: SectionEngagementTrackerProps) {
  const viewedRef = useRef(new Set<string>());
  /** Keys: `${sectionType}:hscroll:${baseId}:${threshold}` */
  const horizontalMilestoneRef = useRef(new Set<string>());

  useEffect(() => {
    const cleanupFns: Array<() => void> = [];

    sections.forEach((config) => {
      const section = document.querySelector<HTMLElement>(config.selector);
      if (!section) return;

      const viewedKey = `${config.sectionType}:viewed`;
      let dwellTimer: ReturnType<typeof setTimeout> | null = null;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry || viewedRef.current.has(viewedKey)) return;

          if (entry.isIntersecting) {
            dwellTimer = setTimeout(() => {
              if (viewedRef.current.has(viewedKey)) return;
              sendEvent(
                config.eventName ?? EventNames.SECTION_VIEWED,
                withSectionType(config.sectionType, config.eventProperties),
              );
              viewedRef.current.add(viewedKey);
            }, config.dwellMs ?? 1500);
          } else if (dwellTimer) {
            clearTimeout(dwellTimer);
            dwellTimer = null;
          }
        },
        { threshold: 0.35 },
      );

      observer.observe(section);
      cleanupFns.push(() => {
        observer.disconnect();
        if (dwellTimer) clearTimeout(dwellTimer);
      });

      if (!config.trackHorizontalScroll) return;

      const baseId = sectionIdFromSelector(config.selector);
      const scrollTarget = document.getElementById(`${baseId}-hscroll`);
      if (
        !scrollTarget ||
        !(scrollTarget instanceof HTMLElement) ||
        !section.contains(scrollTarget)
      ) {
        return;
      }

      const milestones = config.horizontalScrollThresholds?.length
        ? config.horizontalScrollThresholds
        : DEFAULT_SCROLL_PERCENTAGE_THRESHOLDS;

      const onScroll = () => {
        const progress = getHorizontalProgress(scrollTarget);
        const percent = progress * 100;

        for (const t of milestones) {
          const key = `${config.sectionType}:hscroll:${baseId}:${t}`;
          if (percent >= t && !horizontalMilestoneRef.current.has(key)) {
            horizontalMilestoneRef.current.add(key);
            sendEvent(
              config.scrollEventName ?? EventNames.SECTION_SCROLLED,
              withSectionType(config.sectionType, {
                percentage_scrolled: t,
                ...config.scrollEventProperties,
              }),
            );
          }
        }
      };

      scrollTarget.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
      cleanupFns.push(() =>
        scrollTarget.removeEventListener("scroll", onScroll),
      );
    });

    return () => {
      cleanupFns.forEach((cleanup) => cleanup());
      viewedRef.current.clear();
      horizontalMilestoneRef.current.clear();
    };
  }, [sections]);

  return null;
}
